import { use, useEffect, useRef } from 'react';
import { WSContext } from '../../../contexts/ws.context';

const useWS = (subscription: string | null, onMessage: (data: any) => void) => {
  const { addSubscription, removeSubscription } = use(WSContext);
  const onMessageRef = useRef(onMessage);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    const handleMessage = (data: any) => {
      onMessageRef.current(data);
    };

    if (subscription) {
      addSubscription(subscription, handleMessage);
    }

    return () => {
      if (subscription) {
        removeSubscription(subscription, handleMessage);
      }
    };
  }, [subscription, addSubscription, removeSubscription]);
};

export const useWSMultiple = (subscriptions: string[], onMessage: (data: any) => void) => {
  const { addSubscription, removeSubscription } = use(WSContext);
  const onMessageRef = useRef(onMessage);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Key on the joined string so a new array with the same members
  // doesn't tear down and re-register every subscription.
  const subscriptionsKey = subscriptions.join('|');

  useEffect(() => {
    const handleMessage = (data: any) => {
      onMessageRef.current(data);
    };

    const subs = subscriptionsKey ? subscriptionsKey.split('|') : [];

    subs.forEach(subscription => {
      addSubscription(subscription, handleMessage);
    });

    return () => {
      subs.forEach(subscription => {
        // The registry removes by callback identity, so the handler must be
        // passed back. Omitting it matches nothing, which leaves this hook's
        // handler registered for the rest of the session and adds another on
        // every remount.
        removeSubscription(subscription, handleMessage);
      });
    };
  }, [subscriptionsKey, addSubscription, removeSubscription]);
};

export default useWS;
